/**
 * src/utils/symbol-mapper.js
 *
 * CoinGecko ID -> exchange symbol mappings for client-side OHLCV fetching.
 * Fallback chain: Binance -> Coinbase -> Kraken -> OKX
 *
 * Keep in sync with server/shared/symbol-map.js (server-side mirror).
 */

// ─── Binance spot symbols ─────────────────────────────────────────────────────
const SYMBOL_MAP = {
  // Major L1s
  bitcoin:                    'BTCUSDT',
  ethereum:                   'ETHUSDT',
  binancecoin:                'BNBUSDT',
  solana:                     'SOLUSDT',
  ripple:                     'XRPUSDT',
  cardano:                    'ADAUSDT',
  'avalanche-2':              'AVAXUSDT',
  polkadot:                   'DOTUSDT',
  'polygon-ecosystem-token':  'POLUSDT',    // POL — MATIC rebrand; MATICUSDT is dead on Binance (frozen since ~Sept 2024)
  chainlink:                  'LINKUSDT',
  near:                       'NEARUSDT',
  algorand:                   'ALGOUSDT',
  cosmos:                     'ATOMUSDT',
  'internet-computer':        'ICPUSDT',
  filecoin:                   'FILUSDT',
  tron:                       'TRXUSDT',
  stellar:                    'XLMUSDT',
  'ethereum-classic':         'ETCUSDT',
  'bitcoin-cash':             'BCHUSDT',
  litecoin:                   'LTCUSDT',
  'hedera-hashgraph':         'HBARUSDT',
  'quant-network':            'QNTUSDT',    // CoinGecko id changed from 'quant' to 'quant-network'
  aptos:                      'APTUSDT',
  sui:                        'SUIUSDT',
  arbitrum:                   'ARBUSDT',
  optimism:                   'OPUSDT',
  'the-open-network':         'TONUSDT',
  bittensor:                  'TAOUSDT',
  'tether-gold':              'XAUTUSDT',
  'pax-gold':                 'PAXGUSDT',

  // DeFi / DEX
  morpho:                     'MORPHOUSDT',
  'lido-dao':                 'LDOUSDT',
  stacks:                     'STXUSDT',
  'jupiter-exchange-solana':  'JUPUSDT',
  raydium:                    'RAYUSDT',
  gmx:                        'GMXUSDT',
  'rocket-pool':              'RPLUSDT',
  eigenlayer:                 'EIGENUSDT',
  wormhole:                   'WUSDT',
  aave:                       'AAVEUSDT',
  uniswap:                    'UNIUSDT',
  'compound-governance-token':'COMPUSDT',
  maker:                      'MKRUSDT',
  'curve-dao-token':          'CRVUSDT',
  '1inch':                    '1INCHUSDT',
  sushi:                      'SUSHIUSDT',
  'yearn-finance':            'YFIUSDT',

  // L2 / Rollups
  ronin:                      'RONUSDT',
  dymension:                  'DYMUSDT',
  'saga-2':                   'SAGAUSDT',
  movement:                   'MOVEUSDT',
  'conflux-token':            'CFXUSDT',
  'akash-network':            'AKTUSDT',
  'io-net':                   'IOUSDT',
  loopring:                   'LRCUSDT',
  'immutable-x':              'IMXUSDT',
  starknet:                   'STRKUSDT',
  zksync:                     'ZKUSDT',
  astar:                      'ASTRUSDT',   // Astar Network (Polkadot L2)

  // RWA / Institutional
  'ondo-finance':             'ONDOUSDT',   // Ondo Finance

  // AI / DePIN
  'virtuals-protocol':        'VIRTUALUSDT',
  grass:                      'GRASSUSDT',
  'fetch-ai':                 'FETUSDT',
  singularitynet:             'AGIXUSDT',
  'render-token':             'RENDERUSDT',

  // Oracles / Infra
  'the-graph':                'GRTUSDT',
  'basic-attention-token':    'BATUSDT',
  'ocean-protocol':           'OCEANUSDT',
  'band-protocol':            'BANDUSDT',
  api3:                       'API3USDT',
  'trust-wallet-token':       'TWTUSDT',
  'ethereum-name-service':    'ENSUSDT',

  // Meme / Community
  dogecoin:                   'DOGEUSDT',
  'shiba-inu':                'SHIBUSDT',
  pepe:                       'PEPEUSDT',
  floki:                      'FLOKIUSDT',
  bonk:                       'BONKUSDT',
  'dogs-2':                   'DOGSUSDT',
  'cat-in-a-dogs-world':      'MEWUSDT',
  '1000sats-ordinals':        '1000SATSUSDT',
  'the-sandbox':              'SANDUSDT',
  decentraland:               'MANAUSDT',
  'axie-infinity':            'AXSUSDT',

  // Gaming
  enjincoin:                  'ENJUSDT',
  gala:                       'GALAUSDT',
  illuvium:                   'ILVUSDT',

  // Storage
  siacoin:                    'SCUSDT',
  arweave:                    'ARUSDT',
  storj:                      'STORJUSDT',

  // AI / Misc
  worldcoin:                  'WLDUSDT',
  'worldcoin-wld':            'WLDUSDT',    // alternate CoinGecko ID variant

  // Misc top 150
  'sei-network':              'SEIUSDT',
  celestia:                   'TIAUSDT',
  'injective-protocol':       'INJUSDT',
  thorchain:                  'RUNEUSDT',
  kava:                       'KAVAUSDT',
  helium:                     'HNTUSDT',
  eos:                        'EOSUSDT',
  iota:                       'IOTAUSDT',
  neo:                        'NEOUSDT',
  waves:                      'WAVESUSDT',
  zilliqa:                    'ZILUSDT',
  harmony:                    'ONEUSDT',
  vechain:                    'VETUSDT',
  'theta-token':              'THETAUSDT',
  'theta-fuel':               'TFUELUSDT',
  chiliz:                     'CHZUSDT',
  flow:                       'FLOWUSDT',
  'mina-protocol':            'MINAUSDT',
  celo:                       'CELOUSDT',
  'elrond-erd-2':             'EGLDUSDT',
  stepn:                      'GMTUSDT',
  'woo-network':              'WOOUSDT',
  blur:                       'BLURUSDT',
  pendle:                     'PENDLEUSDT',
  jito:                       'JTOUSDT',
  'pyth-network':             'PYTHUSDT',
  dogwifcoin:                 'WIFUSDT',
  popcat:                     'POPCATUSDT',
  notcoin:                    'NOTUSDT',
  'aster-2':                  'ASTERUSDT',  // Aster (perp DEX) — distinct from 'astar' (Astar Network)
  ethena:                     'ENAUSDT',
  sky:                        'SKYUSDT',    // Sky (MakerDAO rebrand)
  just:                       'JSTUSDT',
  nexo:                       'NEXOUSDT',
  dash:                       'DASHUSDT',
  'pancakeswap-token':        'CAKEUSDT',
  'aerodrome-finance':        'AEROUSDT',
  'pump-fun':                 'PUMPUSDT',
};

// ─── NOT on Binance spot ──────────────────────────────────────────────────────
const NOT_ON_BINANCE = new Set([
  'leo-token',          // UNUS SED LEO — OKX / Bitfinex
  'okb',                // OKX exchange token
  'huobi-token',
  'htx-dao',
  'crypto-com-chain',   // CRO — delisted from Binance
  'wrapped-bitcoin',    // WBTC
  'staked-ether',       // stETH
  'wrapped-steth',      // wstETH
  'weth',
  'bitget-token',       // BGB
  'gatechain-token',    // GT — CoinGecko id is 'gatechain-token', not 'gate-token'
  'mexc-token',         // MX
  'whitebit',           // WBT — Kraken-listed
  'figure-heloc',
  'canton-network',
  'memecore',
  'hyperliquid',        // HYPE — Binance futures only
  'blackrock-usd-institutional-digital-liquidity-fund',
  'world-liberty-financial',
  'falcon-finance',
  'rain',
  'monero',             // XMR — delisted from Binance Feb 2024
  'zcash',              // ZEC — delisted from Binance Feb 2024
  'kaspa',              // KAS — never listed on Binance spot (-1121 Invalid symbol) — Kraken only
  'venice-token',       // VVV — not on Binance (confirmed via CoinGecko tickers) — Coinbase-listed
  'the-open-network',   // TON — Binance TONUSDT frozen since 2026-06-30 (still returns old candles) — Coinbase
  'mantle',             // MNT — fully delisted from Binance spot (no MNT pairs at all) — Coinbase/Kraken only
  'lighter',            // Coinbase/Kraken list it as LIGHTER — Binance's "LIT" is an unrelated token, not this one
]);

// ─── Stablecoins ──────────────────────────────────────────────────────────────
const STABLECOIN_IDS = new Set([
  'tether', 'usd-coin', 'binance-usd', 'dai', 'true-usd', 'frax',
  'pax-dollar', 'gemini-dollar', 'first-digital-usd', 'usdd', 'crvusd',
  'ethena-usde', 'paypal-usd', 'usds', 'usd1-wlfi', 'global-dollar',
  'hashnote-usyc', 'ondo-us-dollar-yield',
]);

// ─── Coinbase fallback (#2) ───────────────────────────────────────────────────
const COINBASE_SYMBOL_MAP = {
  'hyperliquid':              'HYPE-USDC',
  'crypto-com-chain':         'CRO-USD',
  'zcash':                    'ZEC-USD',
  'wrapped-bitcoin':          'WBTC-USD',
  'world-liberty-financial':  'WLFI-USD',
  'tether-gold':              'XAUT-USD',   // Coinbase fallback if Binance XAUTUSDT fails
  'venice-token':             'VVV-USD',    // VVV — not on Binance, Coinbase is the deepest venue
  'the-open-network':         'TON-USD',    // TON — Binance pair frozen since 2026-06-30
  'mantle':                   'MANTLE-USD', // MNT — Binance delisted it; Coinbase lists the base asset as "MANTLE"
  'flare-networks':           'FLR-USD',    // FLR — not on Binance
  'lighter':                  'LIGHTER-USD', // Coinbase's base asset is "LIGHTER", not "LIT"
};

// ─── Kraken fallback (#3) ─────────────────────────────────────────────────────
const KRAKEN_SYMBOL_MAP = {
  'monero':       'XMRUSD',
  'zcash':        'ZECUSD',
  'kaspa':        'KASUSD',   // KAS — not on Binance or Coinbase, Kraken-only
  'memecore':     'MUSD',     // M — not on Binance or Coinbase, Kraken-only
  'bitget-token': 'BGBUSD',   // BGB — not on Binance or Coinbase, Kraken-only
  'pi-network':   'PIUSD',    // PI — not on Binance or Coinbase; Kraken over OKX per waterfall priority
  'stable-2':     'STABLEUSD', // STABLE — not on Binance/Coinbase/OKX, Kraken-only
  'whitebit':     'WBTUSD',    // WBT — confirmed via CoinGecko tickers; not listed on OKX, Kraken has it
};

// ─── OKX fallback (#4) ───────────────────────────────────────────────────────
const OKX_SYMBOL_MAP = {
  'okb':              'OKB-USDT',
  'leo-token':        'LEO-USDT',
  'crypto-com-chain': 'CRO-USDT',   // Cronos — delisted from Binance, OKX backup
};

// ─── Accessors ────────────────────────────────────────────────────────────────

export function getBinanceSymbol(coinGeckoId) {
  if (isStablecoin(coinGeckoId))       return null;
  if (NOT_ON_BINANCE.has(coinGeckoId)) return null;
  const mapped = SYMBOL_MAP[coinGeckoId];
  if (!mapped) console.log(`[symbol-mapper] No Binance mapping for "${coinGeckoId}"`);
  return mapped || null;
}

export function isStablecoin(coinGeckoId) {
  return STABLECOIN_IDS.has(coinGeckoId);
}

export function getCoinbaseSymbol(coinGeckoId) {
  return COINBASE_SYMBOL_MAP[coinGeckoId] || null;
}

export function getKrakenSymbol(coinGeckoId) {
  return KRAKEN_SYMBOL_MAP[coinGeckoId] || null;
}

export function getOKXSymbol(coinGeckoId) {
  return OKX_SYMBOL_MAP[coinGeckoId] || null;
}

export function buildSymbolIndex(coins) {
  return coins.reduce((acc, coin) => {
    const s = getBinanceSymbol(coin.id);
    if (s) acc.push({ coinGeckoId: coin.id, binanceSymbol: s });
    return acc;
  }, []);
}

export function getBinanceSymbols(coins) {
  return buildSymbolIndex(coins).map(e => e.binanceSymbol);
}
